import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Cpu, Building2, HardDrive } from "lucide-react";
import { useMe } from "@/hooks/useMe";

const FIRMWARE_PERM = "dashboard.admin.firmware.write";
const ADMIN_PERM = "dashboard.admin";

export function AdminMenu() {
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const perms = me?.permissions ?? [];
  const isAdmin = perms.includes("admin");
  const canFirmware = isAdmin || perms.includes(FIRMWARE_PERM);
  const canManageOrgs = isAdmin || perms.includes(ADMIN_PERM);

  if (!canFirmware && !canManageOrgs) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Admin tools"
        title="Admin tools"
        className="h-9 w-9 rounded-full flex items-center justify-center text-text hover:bg-brand/10 transition-colors cursor-pointer"
      >
        <ShieldCheck size={18} />
      </button>
      {open && (
        <div className="absolute right-0 mt-xs w-56 rounded-md border border-border bg-surface shadow-card overflow-hidden z-30">
          <div className="px-md py-xs text-[10px] uppercase tracking-wider text-textLight border-b border-border">
            Admin
          </div>
          {canManageOrgs && (
            <Link
              to="/admin/organizations"
              onClick={() => setOpen(false)}
              className="flex items-center gap-sm px-md py-sm text-sm text-text hover:bg-bg cursor-pointer"
            >
              <Building2 size={14} className="text-brand" />
              Organizations
            </Link>
          )}
          {canManageOrgs && (
            <Link
              to="/admin/devices"
              onClick={() => setOpen(false)}
              className="flex items-center gap-sm px-md py-sm text-sm text-text hover:bg-bg cursor-pointer"
            >
              <HardDrive size={14} className="text-brand" />
              Devices
            </Link>
          )}
          {canFirmware && (
            <Link
              to="/admin/firmware"
              onClick={() => setOpen(false)}
              className="flex items-center gap-sm px-md py-sm text-sm text-text hover:bg-bg cursor-pointer"
            >
              <Cpu size={14} className="text-brand" />
              Firmware releases
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
